import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { fieldDir } from '../i18n/field-dir';
import { t } from '../services/language-manager';
import {
  CUSTOM_FIELD_ATTRIBUTE_KEY_MAX_LENGTH,
  CUSTOM_FIELD_KEY_MAX_LENGTH,
  CUSTOM_FIELD_KEY_RE,
  CUSTOM_FIELD_LABEL_MAX_LENGTH,
  CUSTOM_FIELD_MAX_LENGTH_DEFAULT,
  CUSTOM_FIELD_MAX_LENGTH_MAX,
  CUSTOM_FIELD_MAX_LENGTH_MIN,
  CUSTOM_FIELD_OPTIONS_MAX,
  CUSTOM_FIELD_PLACEHOLDER_MAX_LENGTH,
  CUSTOM_FIELDS_MAX_TOTAL,
  CUSTOM_FIELDS_MAX_VISIBLE,
  type CustomFieldMode,
  type CustomFieldType,
} from '../services/wizard-backend';
import {
  countVisibleCustomFields,
  emptyCustomField,
  emptyCustomFieldOption,
  isParticipantDataField,
  slugFromLabel,
  uniqueOptionKey,
  validateCustomFields,
  type CustomFieldInput,
  type CustomFieldValueSource,
  type EditableCustomField,
  type EditableCustomFieldOption,
} from '../services/actions-custom-fields';
import { isSendFiles } from '../services/action-types';
import { confirmDialog } from './ConfirmDialog';

/** A 400 CUSTOM_FIELDS_INVALID from the server, resolved to this action. */
export interface CustomFieldsServerError {
  /** Index into the action's customFields array; absent = list-level problem. */
  index?: number;
  field?: string;
  message: string;
}

interface CustomFieldsEditorProps {
  /** Parent action key; namespaces the input ids. */
  actionKey: string;
  /** Parent action type; send-files reads its own copy where the order wording does not fit. */
  actionType: string;
  fields: EditableCustomField[];
  onChange: (fields: EditableCustomField[]) => void;
  serverError: CustomFieldsServerError | null;
  /** True after a failed save: show every problem, not only touched inputs. */
  showAllErrors: boolean;
}

const MODES: CustomFieldMode[] = ['agent', 'fixed', 'hidden'];
const TYPES: CustomFieldType[] = ['text', 'dropdown'];
const MODE_LABEL_KEYS: Record<CustomFieldMode, string> = {
  agent: 'settings-actions-fields-mode-agent',
  fixed: 'settings-actions-fields-mode-fixed',
  hidden: 'settings-actions-fields-mode-hidden',
};
const MODE_HELP_KEYS: Record<CustomFieldMode, string> = {
  agent: 'settings-actions-fields-mode-agent-help',
  fixed: 'settings-actions-fields-mode-fixed-help',
  hidden: 'settings-actions-fields-mode-hidden-help',
};
const TYPE_LABEL_KEYS: Record<CustomFieldType, string> = {
  text: 'settings-actions-fields-type-text',
  dropdown: 'settings-actions-fields-type-dropdown',
};
const SOURCES: CustomFieldValueSource[] = ['admin', 'participantData'];
const SOURCE_LABEL_KEYS: Record<CustomFieldValueSource, string> = {
  admin: 'settings-actions-fields-source-admin',
  participantData: 'settings-actions-fields-source-participant',
};
const SOURCE_HELP_KEYS: Record<CustomFieldValueSource | '', string> = {
  '': 'settings-actions-fields-source-empty-help',
  admin: 'settings-actions-fields-source-admin-help',
  participantData: 'settings-actions-fields-source-participant-help',
};
const INPUTS: ReadonlySet<string> = new Set<CustomFieldInput>([
  'key', 'label', 'value', 'placeholder', 'maxLength', 'type', 'options', 'source', 'attributeKey',
]);

function ChevronIcon({ up }: { up: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points={up ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
    </svg>
  );
}

/** Anything typed or chosen on a new field, so Cancel asks before discarding it. */
function hasFieldInput(field: EditableCustomField): boolean {
  return Boolean(
    field.key.trim() || field.label.trim() || field.value.trim() || field.placeholder.trim()
    || field.source || field.attributeKey.trim() || field.type !== 'text' || field.required
    || field.maxLength !== CUSTOM_FIELD_MAX_LENGTH_DEFAULT
    || field.options.some((o) => o.key.trim() || o.label.trim()),
  );
}

// Leaving a field input for a Cancel / discard control or its confirm must not
// mark it touched: the error it reveals shifts the button out from under the
// pointer, so the first click validates instead of cancelling.
const DISCARD_FOCUS_SELECTOR = '[data-discard], [role="alertdialog"]';

/**
 * For every Cancel / discard button near custom fields (new field, Add action
 * dialog, saved card). Mousedown keeps focus in the input so no blur fires
 * (Safari never focuses a clicked button); keyboard focus is caught by the selector.
 */
export const discardButtonProps = {
  'data-discard': '',
  onMouseDown: (e: MouseEvent<HTMLButtonElement>) => e.preventDefault(),
};

/** Any client problem on the row or its options (shown on the summary, touched or not). */
function rowHasProblem(validation: ReturnType<typeof validateCustomFields>, index: number): boolean {
  const row = validation.rows[index];
  if (row && Object.values(row).some(Boolean)) return true;
  return (validation.optionRows[index] ?? []).some((option) => option && Object.values(option).some(Boolean));
}

function applyFieldType(field: EditableCustomField, type: CustomFieldType): EditableCustomField {
  if (type === field.type) return field;
  if (type === 'dropdown') {
    const options = field.options.length >= 2 ? field.options : [emptyCustomFieldOption(), emptyCustomFieldOption()];
    return {
      ...field,
      type,
      placeholder: '',
      options,
      value: options.some((o) => o.key.trim() === field.value.trim()) ? field.value : '',
    };
  }
  // Options stay on the editable state (the wire shape drops them for text)
  // so dropdown > text > dropdown does not lose the list.
  return { ...field, type: 'text' };
}

/**
 * Participant data is text only (server rule), so choosing it on a dropdown
 * turns the field into text and drops the options and the selected option key.
 */
function applyFieldSource(field: EditableCustomField, source: CustomFieldValueSource): EditableCustomField {
  if (source === 'participantData' && field.type === 'dropdown') {
    return { ...field, source, type: 'text', options: [], value: '' };
  }
  return { ...field, source };
}

/**
 * Replace one option; when its key changes, a default that pointed at the old
 * key follows it (removal clears the default separately).
 */
function patchOption(
  field: EditableCustomField,
  index: number,
  next: EditableCustomFieldOption,
): Partial<EditableCustomField> {
  const oldKey = field.options[index].key;
  const options = field.options.map((o, i) => (i === index ? next : o));
  const value = field.value === oldKey && next.key !== oldKey ? next.key : field.value;
  return { options, value };
}

function applyOptionLabel(
  field: EditableCustomField,
  index: number,
  label: string,
): Partial<EditableCustomField> {
  const current = field.options[index];
  if (current.keyEdited) return patchOption(field, index, { ...current, label });
  const slug = uniqueOptionKey(slugFromLabel(label), field.options, index);
  return patchOption(field, index, { ...current, label, key: slug });
}

/** Options that can be a default: valid keys, first occurrence wins. */
function selectableOptions(options: ReadonlyArray<EditableCustomFieldOption>): Array<{ key: string; label: string }> {
  const seen = new Set<string>();
  const result: Array<{ key: string; label: string }> = [];
  for (const o of options) {
    const key = o.key.trim();
    if (!CUSTOM_FIELD_KEY_RE.test(key) || seen.has(key)) continue;
    seen.add(key);
    result.push({ key, label: o.label.trim() });
  }
  return result;
}

/**
 * The "Custom fields" block of an action card: ordered list of admin-defined
 * extra order fields with reorder / remove, live caps counter, per-input
 * validation, and placement of a server-side CUSTOM_FIELDS_INVALID next to
 * the offending input when the server points at one. Each field is a
 * collapsed summary row; one row's editor is open at a time, and a failed
 * save opens the first row with a problem. Add field opens a new field whose
 * only controls are Save field (validates that row; the action is still saved
 * by its own button) and Cancel; other rows wait until it is saved or dropped.
 */
export function CustomFieldsEditor({
  actionKey,
  actionType,
  fields,
  onChange,
  serverError,
  showAllErrors,
}: CustomFieldsEditorProps) {
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  // Once a save has failed, keep showing every problem on the rows that were
  // there at that save while the admin fixes them (the parent clears its error
  // flag on the first edit). A row added afterwards stays quiet until touched
  // or the next failed save.
  const [submittedRows, setSubmittedRows] = useState<Set<string>>(() => new Set());
  // The one row whose editor is open (a new, unsaved field always is).
  const [openRow, setOpenRow] = useState<string | null>(null);
  // Focus target applied after the next render: a row's summary, its first
  // marked input, its key input, or (after Cancel) the Add field button.
  const [focusTarget, setFocusTarget] = useState<{ rowKey?: string; to: 'summary' | 'invalid' | 'key' | 'add' } | null>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const pendingIndex = fields.findIndex((f) => f.unsaved);
  const pending = pendingIndex >= 0 ? fields[pendingIndex] : null;
  useEffect(() => {
    if (!showAllErrors && !serverError) return;
    setSubmittedRows((prev) => {
      const next = new Set(prev);
      for (const field of fields) {
        next.add(field.rowKey);
        for (const option of field.options) next.add(option.rowKey);
      }
      return next;
    });
    // Open the row the problem is on so its marked input can take the focus;
    // an unsaved new field stays the open one.
    const failed = validateCustomFields(fields);
    const target = pendingIndex >= 0
      ? pendingIndex
      : serverError?.index !== undefined
        ? serverError.index
        : fields.findIndex((_, i) => rowHasProblem(failed, i));
    if (target >= 0 && fields[target]) setOpenRow(fields[target].rowKey);
    // Snapshot the rows at the moment a save fails, not on every edit.
  }, [showAllErrors, serverError]);
  useEffect(() => {
    if (!focusTarget) return;
    setFocusTarget(null);
    if (focusTarget.to === 'add') {
      addRef.current?.focus();
      return;
    }
    const base = `action-${actionKey}-field-${focusTarget.rowKey}`;
    if (focusTarget.to === 'invalid') {
      document.getElementById(`${base}-body`)?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      return;
    }
    document.getElementById(`${base}-${focusTarget.to}`)?.focus();
  }, [focusTarget, actionKey]);
  // Rows whose dropdown was turned into text by choosing participant data;
  // their Type hint says what was removed.
  const [convertedRows, setConvertedRows] = useState<Set<string>>(() => new Set());

  // Order and return share one wording; only send-files has `-files` variants.
  const copyKey = (base: string) => (isSendFiles(actionType) ? `${base}-files` : base);

  const validation = validateCustomFields(fields);
  const visible = countVisibleCustomFields(fields);
  const atTotalCap = fields.length >= CUSTOM_FIELDS_MAX_TOTAL;

  const update = (index: number, patch: Partial<EditableCustomField>) => {
    onChange(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  const remove = (index: number) => {
    if (fields[index].rowKey === openRow) setOpenRow(null);
    onChange(fields.filter((_, i) => i !== index));
  };
  const changeSource = (index: number, source: CustomFieldValueSource) => {
    const field = fields[index];
    const converting = source === 'participantData' && field.type === 'dropdown';
    if (converting || convertedRows.has(field.rowKey)) {
      setConvertedRows((prev) => {
        const next = new Set(prev);
        if (converting) next.add(field.rowKey);
        else next.delete(field.rowKey);
        return next;
      });
    }
    update(index, applyFieldSource(field, source));
  };
  const add = () => {
    if (atTotalCap || pending) return;
    const next = { ...emptyCustomField(fields), unsaved: true };
    onChange([...fields, next]);
    setOpenRow(next.rowKey);
    setFocusTarget({ rowKey: next.rowKey, to: 'key' });
  };
  /** Save field: this row's checks only; on success it becomes a saved, collapsed row. */
  const saveField = (index: number) => {
    const field = fields[index];
    if (rowHasProblem(validateCustomFields(fields), index)) {
      setSubmittedRows((prev) => {
        const next = new Set(prev).add(field.rowKey);
        for (const option of field.options) next.add(option.rowKey);
        return next;
      });
      setFocusTarget({ rowKey: field.rowKey, to: 'invalid' });
      return;
    }
    onChange(fields.map((f, i) => (i === index ? { ...f, unsaved: undefined } : f)));
    setOpenRow(null);
    setFocusTarget({ rowKey: field.rowKey, to: 'summary' });
  };
  /** Cancel on a new field: drop it, asking first if anything was entered. */
  const cancelField = async (index: number) => {
    const field = fields[index];
    if (hasFieldInput(field)) {
      const discard = await confirmDialog({
        title: t('settings-actions-fields-discard-title'),
        subject: field.label.trim() || field.key.trim() || undefined,
        message: t('settings-actions-fields-discard-message'),
        confirmLabel: t('settings-actions-discard'),
        cancelLabel: t('settings-actions-discard-keep'),
        danger: true,
      });
      if (!discard) return;
    }
    setOpenRow(null);
    onChange(fields.filter((f) => f.rowKey !== field.rowKey));
    setFocusTarget({ to: 'add' });
  };

  const touchId = (rowKey: string, input: string) => `${rowKey}:${input}`;
  const markTouched = (rowKey: string, input: string, nextFocus: EventTarget | null) => {
    if ((nextFocus as Element | null)?.closest?.(DISCARD_FOCUS_SELECTOR)) return;
    const id = touchId(rowKey, input);
    if (touched.has(id)) return;
    setTouched((prev) => new Set(prev).add(id));
  };

  /**
   * Client problem for one input, shown once the admin has been there (or after
   * a save). `alwaysShow` is for inputs with no single control to touch (options).
   */
  const clientError = (index: number, input: CustomFieldInput, rawValue: string, alwaysShow = false): string | null => {
    const key = validation.rows[index]?.[input];
    if (!key) return null;
    const rowKey = fields[index].rowKey;
    const show = alwaysShow || submittedRows.has(rowKey) || rawValue !== '' || touched.has(touchId(rowKey, input));
    return show ? t(key) : null;
  };
  /** Server problem placed on this input, if the server pointed at it. */
  const serverErrorFor = (index: number, input: CustomFieldInput): string | null => {
    if (!serverError || serverError.index !== index || serverError.field !== input) return null;
    return serverError.message;
  };
  const inputError = (index: number, input: CustomFieldInput, rawValue: string, alwaysShow = false): string | null => (
    serverErrorFor(index, input) ?? clientError(index, input, rawValue, alwaysShow)
  );
  /** Server problem on a row that names no input we render (e.g. mode). */
  const rowServerError = (index: number): string | null => {
    if (!serverError || serverError.index !== index) return null;
    if (serverError.field && INPUTS.has(serverError.field)) return null;
    return serverError.message;
  };
  const blockError = serverError && serverError.index === undefined
    ? serverError.message
    : validation.block ? t(validation.block) : null;

  const optionError = (
    fieldIndex: number,
    optIndex: number,
    input: 'key' | 'label',
    rawValue: string,
  ): string | null => {
    const key = validation.optionRows[fieldIndex]?.[optIndex]?.[input];
    if (!key) return null;
    const optionRowKey = fields[fieldIndex].options[optIndex].rowKey;
    const show = submittedRows.has(optionRowKey) || rawValue !== '' || touched.has(touchId(optionRowKey, input));
    return show ? t(key) : null;
  };

  const counterText = t('settings-actions-fields-counter', {
    visible: String(visible),
    maxVisible: String(CUSTOM_FIELDS_MAX_VISIBLE),
    total: String(fields.length),
    maxTotal: String(CUSTOM_FIELDS_MAX_TOTAL),
  });
  const overCap = visible > CUSTOM_FIELDS_MAX_VISIBLE || fields.length > CUSTOM_FIELDS_MAX_TOTAL;

  return (
    <div className="form-group custom-fields">
      <div className="custom-fields__header">
        <span className="form-label custom-fields__label">{t('settings-actions-fields-label')}</span>
        <span className={`form-counter${overCap ? ' form-counter--over' : ''}`} aria-live="polite">
          {counterText}
        </span>
      </div>
      <p className="form-hint">{t(copyKey('settings-actions-fields-help'))}</p>

      {fields.length === 0 && (
        <p className="custom-fields__empty">{t('settings-actions-fields-empty')}</p>
      )}

      {fields.map((field, index) => {
        const id = (input: string) => `action-${actionKey}-field-${field.rowKey}-${input}`;
        const isAgent = field.mode === 'agent';
        const isDropdown = field.type === 'dropdown';
        const isSourced = isParticipantDataField(field);
        const sourceError = inputError(index, 'source', field.source);
        const attributeKeyError = isSourced ? inputError(index, 'attributeKey', field.attributeKey) : null;
        const keyError = inputError(index, 'key', field.key);
        const labelError = inputError(index, 'label', field.label);
        const valueError = inputError(index, 'value', field.value);
        const placeholderError = isAgent && !isDropdown ? inputError(index, 'placeholder', field.placeholder) : null;
        const maxLengthError = !isDropdown ? inputError(index, 'maxLength', Number.isNaN(field.maxLength) ? '' : String(field.maxLength)) : null;
        const typeError = inputError(index, 'type', field.type);
        const optionsError = isDropdown ? inputError(index, 'options', '', true) : null;
        const rowError = rowServerError(index);
        const validOptionKeys = selectableOptions(field.options);
        const atOptionCap = field.options.length >= CUSTOM_FIELD_OPTIONS_MAX;
        const isNew = Boolean(field.unsaved);
        const open = isNew || openRow === field.rowKey;
        const incomplete = !open && (rowHasProblem(validation, index) || serverError?.index === index);
        // A new field keeps the focus of the list until it is saved or cancelled.
        const waiting = Boolean(pending) && !isNew;

        return (
          <div className={`custom-field${open ? ' custom-field--open' : ''}`} key={field.rowKey}>
            <div className="custom-field__toolbar">
              <button
                type="button"
                id={id('summary')}
                className="custom-field__summary"
                aria-expanded={open}
                aria-controls={id('body')}
                disabled={isNew || waiting}
                title={waiting ? t('settings-actions-fields-finish-first') : undefined}
                onClick={() => setOpenRow(open ? null : field.rowKey)}
              >
                <svg
                  className={`custom-field__chevron${open ? ' custom-field__chevron--open' : ''}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                <span className="custom-field__summary-text">
                  <span className={`custom-field__summary-key${field.key ? '' : ' custom-field__summary-key--new'}`}>
                    {field.key || t('settings-actions-fields-new')}
                  </span>
                  {field.label.trim() && (
                    <span className="custom-field__summary-label"><bdi>{field.label.trim()}</bdi></span>
                  )}
                  <span className="custom-field__summary-meta">
                    {isSourced ? t('settings-actions-fields-summary-participant') : t(TYPE_LABEL_KEYS[field.type])}
                  </span>
                </span>
                {incomplete && (
                  <span className="custom-field__badge">{t('settings-actions-fields-incomplete')}</span>
                )}
              </button>
              {!isNew && (
              <div className="custom-field__controls">
                <button
                  type="button"
                  className="custom-field__icon-btn"
                  aria-label={t('settings-actions-fields-move-up')}
                  title={t('settings-actions-fields-move-up')}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ChevronIcon up />
                </button>
                <button
                  type="button"
                  className="custom-field__icon-btn"
                  aria-label={t('settings-actions-fields-move-down')}
                  title={t('settings-actions-fields-move-down')}
                  disabled={index === fields.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ChevronIcon up={false} />
                </button>
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={() => remove(index)}
                >
                  {t('settings-actions-fields-remove')}
                </button>
              </div>
              )}
            </div>

            {open && (
            <div id={id('body')} className="custom-field__body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor={id('key')}>
                  {t('settings-actions-fields-key-label')}
                </label>
                <input
                  id={id('key')}
                  dir={fieldDir(field.key, t('settings-actions-fields-key-placeholder'), true)}
                  className={`form-input${keyError ? ' form-input--invalid' : ''}`}
                  type="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={CUSTOM_FIELD_KEY_MAX_LENGTH}
                  value={field.key}
                  placeholder={t('settings-actions-fields-key-placeholder')}
                  aria-invalid={Boolean(keyError)}
                  onChange={(e) => update(index, { key: e.target.value.trim().toLowerCase() })}
                  onBlur={(e) => markTouched(field.rowKey, 'key', e.relatedTarget)}
                />
                {keyError
                  ? <p className="form-error" role="alert">{keyError}</p>
                  : <p className="form-hint">{t('settings-actions-fields-key-help')}</p>}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor={id('label')}>
                  {t('settings-actions-fields-label-label')}
                </label>
                <input
                  id={id('label')}
                  dir={fieldDir(field.label, t('settings-actions-fields-label-placeholder'))}
                  className={`form-input${labelError ? ' form-input--invalid' : ''}`}
                  type="text"
                  maxLength={CUSTOM_FIELD_LABEL_MAX_LENGTH}
                  value={field.label}
                  placeholder={t('settings-actions-fields-label-placeholder')}
                  aria-invalid={Boolean(labelError)}
                  onChange={(e) => update(index, { label: e.target.value })}
                  onBlur={(e) => markTouched(field.rowKey, 'label', e.relatedTarget)}
                />
                <div className="form-hint-row">
                  {labelError
                    ? <p className="form-error" role="alert">{labelError}</p>
                    : <p className="form-hint">{t('settings-actions-fields-label-help')}</p>}
                  <span className="form-counter" aria-live="polite">
                    {field.label.length}/{CUSTOM_FIELD_LABEL_MAX_LENGTH}
                  </span>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor={id('source')}>
                  {t('settings-actions-fields-source-label')}
                </label>
                <select
                  id={id('source')}
                  className={`form-select${sourceError ? ' form-input--invalid' : ''}`}
                  value={field.source}
                  aria-invalid={Boolean(sourceError)}
                  onChange={(e) => changeSource(index, e.target.value as CustomFieldValueSource)}
                  onBlur={(e) => markTouched(field.rowKey, 'source', e.relatedTarget)}
                >
                  {/* No preselection on a new field: the admin picks a source deliberately. */}
                  <option value="" disabled>
                    {t('settings-actions-fields-source-placeholder')}
                  </option>
                  {SOURCES.map((source) => (
                    <option key={source} value={source}>{t(SOURCE_LABEL_KEYS[source])}</option>
                  ))}
                </select>
                {sourceError
                  ? <p className="form-error" role="alert">{sourceError}</p>
                  : <p className="form-hint">{t(SOURCE_HELP_KEYS[field.source])}</p>}
              </div>
              {isSourced && (
                <div className="form-group">
                  <label className="form-label" htmlFor={id('attribute-key')}>
                    {t('settings-actions-fields-attribute-key-label')}
                  </label>
                  <input
                    id={id('attribute-key')}
                    dir={fieldDir(field.attributeKey, t('settings-actions-fields-attribute-key-placeholder'))}
                    className={`form-input${attributeKeyError ? ' form-input--invalid' : ''}`}
                    type="text"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={CUSTOM_FIELD_ATTRIBUTE_KEY_MAX_LENGTH}
                    value={field.attributeKey}
                    placeholder={t('settings-actions-fields-attribute-key-placeholder')}
                    aria-invalid={Boolean(attributeKeyError)}
                    onChange={(e) => update(index, { attributeKey: e.target.value })}
                    onBlur={(e) => markTouched(field.rowKey, 'attributeKey', e.relatedTarget)}
                  />
                  <div className="form-hint-row">
                    {attributeKeyError
                      ? <p className="form-error" role="alert">{attributeKeyError}</p>
                      : <p className="form-hint">{t('settings-actions-fields-attribute-key-help')}</p>}
                    <span className="form-counter" aria-live="polite">
                      {field.attributeKey.length}/{CUSTOM_FIELD_ATTRIBUTE_KEY_MAX_LENGTH}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor={id('type')}>
                  {t('settings-actions-fields-type-label')}
                </label>
                <select
                  id={id('type')}
                  className={`form-select${typeError ? ' form-input--invalid' : ''}`}
                  value={field.type}
                  disabled={isSourced}
                  aria-invalid={Boolean(typeError)}
                  onChange={(e) => update(index, applyFieldType(field, e.target.value as CustomFieldType))}
                >
                  {TYPES.map((type) => (
                    <option key={type} value={type}>{t(TYPE_LABEL_KEYS[type])}</option>
                  ))}
                </select>
                {typeError
                  ? <p className="form-error" role="alert">{typeError}</p>
                  : (
                    // Live so a dropdown turned into text by the source change is announced.
                    <p className="form-hint" aria-live="polite">
                      {isSourced
                        ? t(convertedRows.has(field.rowKey)
                          ? 'settings-actions-fields-type-participant-converted'
                          : 'settings-actions-fields-type-participant-help')
                        : t(isDropdown ? 'settings-actions-fields-type-dropdown-help' : 'settings-actions-fields-type-text-help')}
                    </p>
                  )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor={id('mode')}>
                  {t('settings-actions-fields-mode-label')}
                </label>
                <select
                  id={id('mode')}
                  className="form-select"
                  value={field.mode}
                  onChange={(e) => update(index, { mode: e.target.value as CustomFieldMode })}
                >
                  {MODES.map((mode) => (
                    <option key={mode} value={mode}>{t(MODE_LABEL_KEYS[mode])}</option>
                  ))}
                </select>
                <p className="form-hint">{t(MODE_HELP_KEYS[field.mode])}</p>
              </div>
            </div>

            {isDropdown && (
              <fieldset className="custom-field__options">
                <legend className="form-label custom-field__options-legend">
                  {t('settings-actions-fields-options-label')}
                </legend>
                <span className="form-counter custom-field__options-counter" aria-live="polite">
                  {t('settings-actions-fields-options-counter', {
                    n: String(field.options.length),
                    max: String(CUSTOM_FIELD_OPTIONS_MAX),
                  })}
                </span>
                <p className="form-hint custom-field__options-help">{t('settings-actions-fields-options-help')}</p>
                {field.options.map((option, optIndex) => {
                  const optId = (input: string) => `${id('opt')}-${option.rowKey}-${input}`;
                  const optKeyError = optionError(index, optIndex, 'key', option.key);
                  const optLabelError = optionError(index, optIndex, 'label', option.label);
                  const optionName = option.label.trim()
                    || t('settings-actions-fields-option-ordinal', { n: String(optIndex + 1) });
                  const removeLabel = t('settings-actions-fields-option-remove-named', { name: optionName });
                  return (
                    <div className="custom-field__option" key={option.rowKey}>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label" htmlFor={optId('label')}>
                            {t('settings-actions-fields-option-label')}
                          </label>
                          <input
                            id={optId('label')}
                            dir={fieldDir(option.label)}
                            className={`form-input${optLabelError ? ' form-input--invalid' : ''}`}
                            type="text"
                            maxLength={CUSTOM_FIELD_LABEL_MAX_LENGTH}
                            value={option.label}
                            aria-invalid={Boolean(optLabelError)}
                            onChange={(e) => update(index, applyOptionLabel(field, optIndex, e.target.value))}
                            onBlur={(e) => markTouched(option.rowKey, 'label', e.relatedTarget)}
                          />
                          {optLabelError && <p className="form-error" role="alert">{optLabelError}</p>}
                        </div>
                        <div className="form-group">
                          <label className="form-label" htmlFor={optId('key')}>
                            {t('settings-actions-fields-option-key')}
                          </label>
                          <input
                            id={optId('key')}
                            dir="ltr"
                            className={`form-input${optKeyError ? ' form-input--invalid' : ''}`}
                            type="text"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            maxLength={CUSTOM_FIELD_KEY_MAX_LENGTH}
                            value={option.key}
                            aria-invalid={Boolean(optKeyError)}
                            onChange={(e) => update(index, patchOption(field, optIndex, {
                              ...option,
                              key: e.target.value.trim().toLowerCase(),
                              keyEdited: true,
                            }))}
                            onBlur={(e) => markTouched(option.rowKey, 'key', e.relatedTarget)}
                          />
                          {optKeyError && <p className="form-error" role="alert">{optKeyError}</p>}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn--secondary btn--small"
                        aria-label={removeLabel}
                        title={removeLabel}
                        onClick={() => update(index, {
                          options: field.options.filter((_, i) => i !== optIndex),
                          value: field.value === option.key.trim() ? '' : field.value,
                        })}
                        disabled={field.options.length <= 2}
                      >
                        {t('settings-actions-fields-option-remove')}
                      </button>
                    </div>
                  );
                })}
                <div className="action-card__actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => {
                      if (atOptionCap) return;
                      update(index, { options: [...field.options, emptyCustomFieldOption()] });
                    }}
                    disabled={atOptionCap}
                  >
                    {t('settings-actions-fields-option-add')}
                  </button>
                  {atOptionCap && (
                    <span className="form-hint action-card__actions-hint">
                      {t('settings-actions-fields-option-add-cap')}
                    </span>
                  )}
                </div>
                {optionsError && <p className="form-error" role="alert">{optionsError}</p>}
              </fieldset>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor={id('value')}>
                  {isSourced
                    ? t('settings-actions-fields-value-fallback-label')
                    : isAgent
                      ? t('settings-actions-fields-value-default-label')
                      : t('settings-actions-fields-value-label')}
                </label>
                {isDropdown ? (
                  <select
                    id={id('value')}
                    className={`form-select${valueError ? ' form-input--invalid' : ''}`}
                    value={field.value}
                    aria-invalid={Boolean(valueError)}
                    onChange={(e) => update(index, { value: e.target.value })}
                    onBlur={(e) => markTouched(field.rowKey, 'value', e.relatedTarget)}
                  >
                    <option value="">{t('settings-actions-fields-default-none')}</option>
                    {validOptionKeys.map((o, optIndex) => (
                      <option key={optIndex} value={o.key}>{o.label || o.key}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={id('value')}
                    dir={fieldDir(field.value)}
                    className={`form-input${valueError ? ' form-input--invalid' : ''}`}
                    type="text"
                    maxLength={CUSTOM_FIELD_MAX_LENGTH_MAX}
                    value={field.value}
                    aria-invalid={Boolean(valueError)}
                    onChange={(e) => update(index, { value: e.target.value })}
                    onBlur={(e) => markTouched(field.rowKey, 'value', e.relatedTarget)}
                  />
                )}
                {valueError
                  ? <p className="form-error" role="alert">{valueError}</p>
                  : (
                    <p className="form-hint">
                      {isSourced
                        ? t(isAgent ? 'settings-actions-fields-value-fallback-agent-help' : 'settings-actions-fields-value-fallback-help')
                        : isDropdown
                          ? (isAgent
                            ? t('settings-actions-fields-value-default-dropdown-help')
                            : t(copyKey('settings-actions-fields-value-dropdown-help')))
                          : (isAgent
                            ? t('settings-actions-fields-value-default-help')
                            : t(copyKey('settings-actions-fields-value-help')))}
                    </p>
                  )}
              </div>
            </div>

            {!isDropdown && (
              <div className="form-row">
                {isAgent && (
                  <div className="form-group">
                    <label className="form-label" htmlFor={id('placeholder')}>
                      {t('settings-actions-fields-placeholder-label')}
                    </label>
                    <input
                      id={id('placeholder')}
                      dir={fieldDir(field.placeholder)}
                      className={`form-input${placeholderError ? ' form-input--invalid' : ''}`}
                      type="text"
                      maxLength={CUSTOM_FIELD_PLACEHOLDER_MAX_LENGTH}
                      value={field.placeholder}
                      aria-invalid={Boolean(placeholderError)}
                      onChange={(e) => update(index, { placeholder: e.target.value })}
                      onBlur={(e) => markTouched(field.rowKey, 'placeholder', e.relatedTarget)}
                    />
                    {placeholderError
                      ? <p className="form-error" role="alert">{placeholderError}</p>
                      : <p className="form-hint">{t('settings-actions-fields-placeholder-help')}</p>}
                  </div>
                )}
                <div className="form-group custom-field__max-length">
                  <label className="form-label" htmlFor={id('max-length')}>
                    {t('settings-actions-fields-max-length-label')}
                  </label>
                  <input
                    id={id('max-length')}
                    className={`form-input${maxLengthError ? ' form-input--invalid' : ''}`}
                    type="number"
                    min={CUSTOM_FIELD_MAX_LENGTH_MIN}
                    max={CUSTOM_FIELD_MAX_LENGTH_MAX}
                    step={1}
                    value={Number.isNaN(field.maxLength) ? '' : field.maxLength}
                    aria-invalid={Boolean(maxLengthError)}
                    onChange={(e) => update(index, {
                      maxLength: e.target.value === '' ? NaN : Number(e.target.value),
                    })}
                    onBlur={(e) => markTouched(field.rowKey, 'maxLength', e.relatedTarget)}
                  />
                  {maxLengthError
                    ? <p className="form-error" role="alert">{maxLengthError}</p>
                    : <p className="form-hint">{t('settings-actions-fields-max-length-help')}</p>}
                </div>
              </div>
            )}

            {(isAgent || isSourced) && (
              <div className="form-group custom-field__required">
                <label className="settings-page__toggle">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) => update(index, { required: e.target.checked })}
                  />
                  <span>{t('settings-actions-fields-required')}</span>
                </label>
                <p className="form-hint">
                  {t(isSourced && !isAgent
                    ? 'settings-actions-fields-required-participant-help'
                    : 'settings-actions-fields-required-help')}
                </p>
              </div>
            )}

            {rowError && (
              <p className="form-error" role="alert">{rowError}</p>
            )}
            {isNew && (
              <div className="custom-field__new-actions">
                <button type="button" className="btn btn--secondary btn--small" {...discardButtonProps} onClick={() => void cancelField(index)}>
                  {t('settings-actions-fields-cancel')}
                </button>
                <button type="button" className="btn btn--primary btn--small" onClick={() => saveField(index)}>
                  {t('settings-actions-fields-save')}
                </button>
              </div>
            )}
            </div>
            )}
          </div>
        );
      })}

      {!pending && (
      <div className="action-card__actions">
        <button
          ref={addRef}
          type="button"
          className="btn btn--primary action-strong-btn"
          onClick={add}
          disabled={atTotalCap}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('settings-actions-fields-add')}
        </button>
        {atTotalCap && (
          <span className="form-hint action-card__actions-hint">
            {t('settings-actions-fields-add-cap')}
          </span>
        )}
      </div>
      )}

      {blockError && (
        <p className="form-error" role="alert">{blockError}</p>
      )}
    </div>
  );
}
